import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ExtendedSiteSettings, HeaderMenuItem, FooterSection, FooterLink } from '@/lib/cms-types';

// Site Settings
export function useExtendedSiteSettings() {
  return useQuery({
    queryKey: ['extended-site-settings'],
    queryFn: async (): Promise<ExtendedSiteSettings | null> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching site settings:', error);
        return null;
      }

      return data as unknown as ExtendedSiteSettings;
    },
    staleTime: 30 * 1000, // 30 seconds - shorter for faster frontend updates
  });
}

export function useUpdateSiteSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: Partial<ExtendedSiteSettings>) => {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .limit(1)
        .single();

      if (!existing) {
        throw new Error('No site settings found');
      }

      const { error } = await supabase
        .from('site_settings')
        .update(settings as any)
        .eq('id', existing.id);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all related queries to ensure frontend updates immediately
      queryClient.invalidateQueries({ queryKey: ['extended-site-settings'] });
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      queryClient.invalidateQueries({ queryKey: ['article-display-settings'] });
    },
  });
}

// Header Menu Items
export function useHeaderMenuItems() {
  return useQuery({
    queryKey: ['header-menu-items'],
    queryFn: async (): Promise<HeaderMenuItem[]> => {
      const { data, error } = await supabase
        .from('header_menu_items')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching header menu items:', error);
        return [];
      }

      return (data || []) as HeaderMenuItem[];
    },
    staleTime: 30 * 1000, // 30 seconds for faster frontend updates
  });
}

export function useCreateHeaderMenuItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (item: Partial<HeaderMenuItem>) => {
      const { error } = await supabase
        .from('header_menu_items')
        .insert(item as any);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['header-menu-items'] });
    },
  });
}

export function useUpdateHeaderMenuItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<HeaderMenuItem> & { id: string }) => {
      const { error } = await supabase
        .from('header_menu_items')
        .update(updates as any)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['header-menu-items'] });
    },
  });
}

export function useDeleteHeaderMenuItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('header_menu_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['header-menu-items'] });
    },
  });
}

export function useReorderHeaderMenuItems() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      const promises = items.map(item =>
        supabase
          .from('header_menu_items')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) throw errors[0].error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['header-menu-items'] });
    },
  });
}

// Footer Sections
export function useFooterSections() {
  return useQuery({
    queryKey: ['footer-sections'],
    queryFn: async (): Promise<FooterSection[]> => {
      const { data: sections, error: sectionsError } = await supabase
        .from('footer_sections')
        .select('*')
        .order('sort_order', { ascending: true });

      if (sectionsError) {
        console.error('Error fetching footer sections:', sectionsError);
        return [];
      }

      const { data: links, error: linksError } = await supabase
        .from('footer_links')
        .select('*')
        .order('sort_order', { ascending: true });

      if (linksError) {
        console.error('Error fetching footer links:', linksError);
        return (sections || []) as FooterSection[];
      }

      const sectionsWithLinks = (sections || []).map(section => ({
        ...section,
        links: (links || []).filter(link => link.section_id === section.id),
      })) as FooterSection[];

      return sectionsWithLinks;
    },
    staleTime: 30 * 1000, // 30 seconds for faster frontend updates
  });
}

export function useCreateFooterSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (section: Partial<FooterSection>) => {
      const { error } = await supabase
        .from('footer_sections')
        .insert(section as any);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}

export function useUpdateFooterSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<FooterSection> & { id: string }) => {
      const { error } = await supabase
        .from('footer_sections')
        .update(updates as any)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}

export function useDeleteFooterSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('footer_sections')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}

// Footer Links
export function useCreateFooterLink() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (link: Partial<FooterLink>) => {
      const { error } = await supabase
        .from('footer_links')
        .insert(link as any);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}

export function useUpdateFooterLink() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<FooterLink> & { id: string }) => {
      const { error } = await supabase
        .from('footer_links')
        .update(updates as any)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}

export function useDeleteFooterLink() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('footer_links')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}

export function useReorderFooterSections() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      const promises = items.map(item =>
        supabase
          .from('footer_sections')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) throw errors[0].error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}

export function useReorderFooterLinks() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      const promises = items.map(item =>
        supabase
          .from('footer_links')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) throw errors[0].error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-sections'] });
    },
  });
}
